import React from 'react';
import { Paper, Box, Typography, Button, LinearProgress, Stack, Chip, CircularProgress } from '@mui/material';
import { useMilestones } from '../../hooks/useMilestones';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../constants/roles';

const MilestonesWidget: React.FC = () => {
  const { auth } = useAuth();
  const isChild = auth.user?.role === ROLES.CHILD;
  const { milestones, loading, error, reload, completeMilestone } = useMilestones(0, { status: 'in_progress' });

  const nextUp = milestones
    .filter(m => m.status !== 'completed')
    .sort((a, b) => {
      const aDue = a.deadline ? new Date(a.deadline).getTime() : Number.MAX_SAFE_INTEGER;
      const bDue = b.deadline ? new Date(b.deadline).getTime() : Number.MAX_SAFE_INTEGER;
      return aDue - bDue;
    })[0];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight="bold">🎯 Milestones & Timeline</Typography>
      <Typography variant="body2" color="text.secondary">Track progress toward your major life goals.</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Box sx={{ mt: 2 }}>
          <Typography color="error.main">{error}</Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={reload}>Retry</Button>
        </Box>
      ) : nextUp ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 2 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" value={nextUp.percentComplete} size={64} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="subtitle2">{nextUp.percentComplete}%</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600}>{nextUp.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {nextUp.type === 'amount'
                ? `Saved $${(nextUp.currentAmount ?? 0).toFixed(0)} / $${(nextUp.targetAmount ?? 0).toFixed(0)}`
                : nextUp.type === 'checklist'
                ? `${(nextUp.checklist || []).filter(i => i.done).length}/${(nextUp.checklist || []).length} tasks`
                : nextUp.deadline ? `Due ${new Date(nextUp.deadline).toLocaleDateString()}` : 'Date-based'}
            </Typography>
            <LinearProgress variant="determinate" value={nextUp.percentComplete} sx={{ mt: 1, height: 8, borderRadius: 1 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {nextUp.linkedCategory && <Chip size="small" label={`Category: ${nextUp.linkedCategory}`} />}
              {nextUp.goalId && <Chip size="small" label={`Goal`} />}
            </Stack>
          </Box>
          <Stack
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            width={{ xs: '100%', sm: 'auto' }}
          >
            <Button size="small" sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => window.location.assign('/milestones')}>View Milestones →</Button>
            {!isChild && nextUp.status !== 'completed' && (
              <Button size="small" variant="contained" sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => completeMilestone(nextUp._id)}>Mark Done</Button>
            )}
          </Stack>
        </Stack>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography color="text.secondary">No milestones yet.</Typography>
          <Button variant="contained" size="small" sx={{ mt: 1 }} onClick={() => window.location.assign('/milestones')}>
            View Milestones
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default MilestonesWidget;
